<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-dialog session-detail-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header" :class="typeClass">
          <div class="header-info">
            <span class="type-badge">{{ typeLabel }}</span>
            <h3>{{ sessionTitle }}</h3>
            <span class="session-datetime">
              <i class="bi bi-calendar3"></i> {{ formatDate(session.matchDate) }}
              <i class="bi bi-clock ms-2"></i> {{ formatTime(session.kickOffTime) }}
              <span v-if="session.endTime"> - {{ formatTime(session.endTime) }}</span>
            </span>
            <span v-if="session.locationName" class="session-location">
              <i class="bi bi-geo-alt"></i> {{ session.locationName }}
            </span>
          </div>
          <button @click="$emit('close')" class="close-button">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Attendance -->
          <div class="attendance-section">
            <h4><i class="bi bi-person-check"></i> Availability</h4>
            <div class="attendance-buttons">
              <button
                class="att-btn att-going"
                :class="{ active: attendanceStatus === 'going' }"
                @click="setAttendance('going')"
              >
                <i class="bi bi-check-circle-fill"></i> Going
              </button>
              <button
                class="att-btn att-not-going"
                :class="{ active: attendanceStatus === 'notGoing' }"
                @click="setAttendance('notGoing')"
              >
                <i class="bi bi-x-circle-fill"></i> Not Going
              </button>
              <button
                class="att-btn att-injured"
                :class="{ active: attendanceStatus === 'injured' }"
                @click="setAttendance('injured')"
              >
                <i class="bi bi-bandaid-fill"></i> Injured
              </button>
            </div>
          </div>

          <!-- Linked training content (programmes expand; drills open full detail) -->
          <div v-if="drills.length > 0" class="drills-section">
            <h4><i class="bi bi-clipboard-check"></i> Session Drills ({{ drills.length }})</h4>
            <SessionContentList :items="drills" />
          </div>

          <!-- Performance Report Form -->
          <div class="report-section">
            <h4><i class="bi bi-bar-chart-line"></i> Session Report</h4>

            <!-- Must mark attendance first -->
            <div v-if="!isAttending && !hasExistingReport" class="attendance-required">
              <i class="bi bi-info-circle"></i>
              <p>Please mark your attendance above before submitting a session report.</p>
            </div>

            <div v-else-if="!isEditing && hasExistingReport" class="report-readonly">
              <div class="report-stat-grid">
                <div class="report-stat">
                  <span class="stat-label">RPE</span>
                  <span class="stat-value rpe-value" :class="rpeClass(reportForm.effort)">{{ reportForm.effort }}/10</span>
                  <span class="stat-sub">{{ rpeDescription(reportForm.effort) }}</span>
                </div>
                <div class="report-stat">
                  <span class="stat-label">Enjoyment</span>
                  <span class="stat-value">{{ reportForm.enjoyment }}</span>
                </div>
                <div v-if="reportForm.duration_mins" class="report-stat">
                  <span class="stat-label">Duration</span>
                  <span class="stat-value">{{ reportForm.duration_mins }} mins</span>
                </div>
              </div>
              <div v-if="reportForm.session_notes" class="report-notes-display">
                <strong>Notes:</strong> {{ reportForm.session_notes }}
              </div>
              <button class="btn btn-sm btn-outline-primary mt-2" @click="isEditing = true">
                <i class="bi bi-pencil"></i> Edit Report
              </button>
            </div>

            <form v-else-if="isAttending || hasExistingReport" @submit.prevent="submitReport" class="report-form">
              <!-- RPE Scale -->
              <div class="form-group">
                <label>Rate of Perceived Exertion (RPE)</label>
                <div class="rpe-scale">
                  <div
                    v-for="item in rpeScale"
                    :key="item.value"
                    class="rpe-scale-item"
                    :class="{ active: reportForm.effort == item.value }"
                    @click="reportForm.effort = item.value"
                  >
                    <span class="rpe-number" :style="{ background: item.color }">{{ item.value }}</span>
                    <span class="rpe-label">{{ item.label }}</span>
                  </div>
                </div>
              </div>

              <!-- Enjoyment -->
              <div class="form-group">
                <label>Did you enjoy the session?</label>
                <div class="enjoyment-buttons">
                  <button
                    v-for="opt in enjoymentOptions"
                    :key="opt.value"
                    type="button"
                    class="enjoyment-btn"
                    :class="{ active: reportForm.enjoyment === opt.value }"
                    @click="reportForm.enjoyment = opt.value"
                  >
                    <span class="enjoyment-emoji">{{ opt.emoji }}</span>
                    <span class="enjoyment-label">{{ opt.label }}</span>
                  </button>
                </div>
              </div>

              <!-- Duration -->
              <div class="form-group">
                <label>Duration (minutes)</label>
                <input type="number" v-model.number="reportForm.duration_mins" class="form-input" placeholder="60" min="1" max="300" />
              </div>

              <!-- Notes -->
              <div class="form-group">
                <label>Session notes (optional)</label>
                <textarea v-model="reportForm.session_notes" class="form-input" rows="3" placeholder="How did the session go?"></textarea>
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="!reportForm.effort || !reportForm.enjoyment || saving"
              >
                <i class="bi bi-check-lg"></i>
                {{ saving ? 'Saving...' : (hasExistingReport ? 'Update Report' : 'Submit Report') }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { TrainingWeek } from "@/services/training-week-service.js";
import SessionContentList from "@/components/SessionContentList/index.vue";

export default {
  name: "SessionDetailModal",
  components: { SessionContentList },
  props: {
    show: { type: Boolean, default: false },
    session: { type: Object, default: () => ({}) },
    drills: { type: Array, default: () => [] },
    userData: { type: Object, default: () => ({}) },
    selectedUserData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isEditing: false,
      saving: false,
      reportForm: {
        effort: null,
        enjoyment: null,
        duration_mins: null,
        session_notes: "",
      },
      rpeScale: [
        { value: 1, label: "Rest", color: "#c3e6fc" },
        { value: 2, label: "Really Easy", color: "#a0d8ef" },
        { value: 3, label: "Easy", color: "#7ecf7e" },
        { value: 4, label: "Moderate", color: "#a8d84e" },
        { value: 5, label: "Somewhat Hard", color: "#d4e034" },
        { value: 6, label: "Hard", color: "#f5d328" },
        { value: 7, label: "Really Hard", color: "#f5a623" },
        { value: 8, label: "Very Hard", color: "#f57723" },
        { value: 9, label: "Really Really Hard", color: "#ef4444" },
        { value: 10, label: "Maximal", color: "#c81e1e" },
      ],
      enjoymentOptions: [
        { value: "Amazing", label: "Amazing", emoji: "🤩" },
        { value: "Good", label: "Good", emoji: "😊" },
        { value: "Ok", label: "Ok", emoji: "😐" },
        { value: "Not so good", label: "Not great", emoji: "😕" },
        { value: "Did not enjoy it", label: "Didn't enjoy", emoji: "😞" },
      ],
    };
  },
  computed: {
    playerId() {
      return this.selectedUserData.id || this.userData.id;
    },
    isMatch() {
      return this.session.gameType !== "Training";
    },
    sessionType() {
      if (this.isMatch) return "match";
      return this.session.training_session_type || "Team Training";
    },
    typeClass() {
      const map = {
        "Team Training": "header-team",
        "Own Training": "header-own",
        "S&C": "header-sc",
        "match": "header-match",
      };
      return map[this.sessionType] || "header-team";
    },
    typeLabel() {
      if (this.isMatch) return this.session.gameType || "Match";
      return this.sessionType;
    },
    sessionTitle() {
      if (this.isMatch) return `${this.session.opposition || "Match"}`;
      return this.session.notes || this.sessionType;
    },
    hasExistingReport() {
      return !!this.session.report_id;
    },
    isPlayer() {
      return this.userData.userType !== "Admin" && this.userData.userType !== "Coach" && this.userData.userType !== "HeadCoach";
    },
    isAttending() {
      return parseInt(this.session.going) === 1;
    },
    attendanceStatus() {
      if (parseInt(this.session.going) === 1) return "going";
      if (parseInt(this.session.notGoing) === 1) return "notGoing";
      if (parseInt(this.session.injured) === 1) return "injured";
      return null;
    },
  },
  watch: {
    session: {
      immediate: true,
      handler(val) {
        if (val && val.report_id) {
          this.reportForm = {
            effort: val.effort,
            enjoyment: val.enjoyment,
            duration_mins: val.duration_mins || this.calcDuration(val),
            session_notes: val.report_notes || val.session_notes || "",
          };
          this.isEditing = false;
        } else {
          this.reportForm = {
            effort: null,
            enjoyment: null,
            duration_mins: this.calcDuration(val),
            session_notes: "",
          };
          this.isEditing = true;
        }
      },
    },
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format("ddd D MMM YYYY") : "";
    },
    formatTime(d) {
      // Wall-clock-UTC contract: read with .utc() or times shift by the
      // viewer's TZ offset (+1h in BST). See views/booking time-storage note.
      return d ? moment(d).utc().format("HH:mm") : "";
    },
    getImgUrl(pic) {
      return pic ? `/images/${pic}` : "";
    },
    calcDuration(session) {
      if (!session || !session.kickOffTime || !session.endTime) return null;
      const diff = moment(session.endTime).diff(moment(session.kickOffTime), "minutes");
      return diff > 0 ? diff : null;
    },
    rpeDescription(n) {
      const item = this.rpeScale.find(r => r.value == n);
      return item ? item.label : "";
    },
    rpeClass(n) {
      if (n >= 9) return "rpe-high";
      if (n >= 7) return "rpe-med-high";
      if (n >= 5) return "rpe-med";
      return "rpe-low";
    },
    async setAttendance(status) {
      const going = status === "going" ? 1 : 0;
      const notGoing = status === "notGoing" ? 1 : 0;
      const injured = status === "injured" ? 1 : 0;
      const noResponse = 0;

      try {
        await TrainingWeek.setAttendance({
          gameId: this.session.id,
          userId: this.playerId,
          kickOffTime: this.session.kickOffTime,
          going,
          notGoing,
          noResponse,
          injured,
          teamId: this.session.teamId,
          gameType: this.session.gameType,
          comments: "",
        });
        // Update local session data for instant UI feedback
        this.session.going = going;
        this.session.notGoing = notGoing;
        this.session.injured = injured;
        this.$emit("attendanceUpdated");
      } catch (err) {
        console.error("Error updating attendance:", err);
      }
    },
    async submitReport() {
      if (!this.reportForm.effort || !this.reportForm.enjoyment) return;
      this.saving = true;
      try {
        await TrainingWeek.savePerformanceReport({
          traningId: this.session.id,
          user_id: this.playerId,
          effort: this.reportForm.effort,
          enjoyment: this.reportForm.enjoyment,
          duration_mins: this.reportForm.duration_mins,
          session_notes: this.reportForm.session_notes,
          training_session_type: this.session.training_session_type || "Team Training",
        });
        this.isEditing = false;
        this.$emit("reportSaved");
      } catch (err) {
        console.error("Error saving report:", err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.session-detail-dialog {
  background: #fff;
  border-radius: 12px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-content {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;

  &.header-team { border-top: 4px solid #3b82f6; }
  &.header-own { border-top: 4px solid #22c55e; }
  &.header-sc { border-top: 4px solid #f59e0b; }
  &.header-match { border-top: 4px solid #ef4444; }

  .header-info {
    h3 { margin: 4px 0; font-size: 1.1rem; font-weight: 600; }
  }

  .type-badge {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  .session-datetime, .session-location {
    display: block;
    font-size: 0.8rem;
    color: #6b7280;
    margin-top: 2px;
    i { font-size: 0.75rem; }
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #9ca3af;
  &:hover { color: #1f2937; }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* Attendance Section */
.attendance-section {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    i { color: #3b82f6; }
  }
}

.attendance-buttons {
  display: flex;
  gap: 8px;
}

.att-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
  background: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: #9ca3af; }

  &.att-going {
    color: #16a34a;
    &.active { background: #f0fdf4; border-color: #16a34a; }
    &:hover { border-color: #16a34a; }
  }

  &.att-not-going {
    color: #dc2626;
    &.active { background: #fef2f2; border-color: #dc2626; }
    &:hover { border-color: #dc2626; }
  }

  &.att-injured {
    color: #d97706;
    &.active { background: #fffbeb; border-color: #d97706; }
    &:hover { border-color: #d97706; }
  }
}

.attendance-required {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  color: #92400e;
  font-size: 0.85rem;

  i { font-size: 1rem; margin-top: 1px; color: #d97706; }
  p { margin: 0; }
}

/* Drills Section */
.drills-section {
  margin-bottom: 20px;

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;
    i { color: #3b82f6; }
  }
}

.drill-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drill-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.drill-thumb {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
}

.drill-name {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
}

.drill-cat {
  font-size: 0.72rem;
  color: #6b7280;
}

/* Report Section */
.report-section {
  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    i { color: #f59e0b; }
  }
}

.report-readonly {
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px;
  border: 1px solid #e5e7eb;
}

.report-stat-grid {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.report-stat {
  .stat-label {
    display: block;
    font-size: 0.7rem;
    color: #9ca3af;
    text-transform: uppercase;
    font-weight: 600;
  }
  .stat-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1f2937;
  }
}

.rpe-value {
  &.rpe-high { color: #ef4444; }
  &.rpe-med-high { color: #f59e0b; }
  &.rpe-med { color: #22c55e; }
  &.rpe-low { color: #3b82f6; }
}

.report-notes-display {
  margin-top: 10px;
  font-size: 0.85rem;
  color: #4b5563;
}

/* Report Form */
.report-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  label {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
  }
}

.rpe-scale {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rpe-scale-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: #f9fafb; }
  &.active {
    border-color: #1f2937;
    background: #f3f4f6;
  }
}

.rpe-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.9rem;
  color: #1f2937;
  flex-shrink: 0;
}

.rpe-label {
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
}

.rpe-scale-item.active .rpe-label {
  font-weight: 700;
  color: #1f2937;
}

.stat-sub {
  display: block;
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.enjoyment-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.enjoyment-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 10px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  flex: 1;
  min-width: 60px;

  &:hover { border-color: #3b82f6; }
  &.active { border-color: #3b82f6; background: #eff6ff; }
}

.enjoyment-emoji { font-size: 1.3rem; }
.enjoyment-label { font-size: 0.6rem; font-weight: 600; color: #6b7280; }

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15); }
}

textarea.form-input { resize: vertical; }

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

@media (max-width: 480px) {
  .session-detail-dialog { max-width: 100%; margin: 0; border-radius: 12px 12px 0 0; align-self: flex-end; }
  .rpe-btn { width: 32px; height: 32px; font-size: 0.8rem; }
  .enjoyment-btn { padding: 6px 6px; min-width: 50px; }
}
</style>
